import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, ElementRef, inject, Input, OnDestroy, OnInit, QueryList, signal, ViewChild, ViewChildren } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { Hotkey } from '../../model/hotkey.model';
import { HotkeysService, keyMap } from '../../services/hotkeys.service';
import { ShortcutRegistryService } from '../../services/shortcut-registry.service';
import type { KeyActionMap } from '../../types-interfaces/hotkey.options';
import { ShortcutInputComponent } from '../shortcut-input/shortcut-input.component';

interface GroupedHotkey {
    category: string;
    hotkeys: Hotkey[];
}

@Component({
    selector: 'hotkeys-cheatsheet',
    templateUrl: './hotkeys-cheatsheet.component.html',
    styleUrls: ['./hotkeys-cheatsheet.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        TranslatePipe,
        ShortcutInputComponent,
    ]
})
export class HotkeysCheatsheetComponent implements OnInit, OnDestroy {
    protected hotkeysService = inject(HotkeysService);
    helpVisible$ = new BehaviorSubject(false);
    @Input() title = 'Keyboard Shortcuts';
    subscription: Subscription;

    private translateService = inject(TranslateService);
    readonly shortcutRegistry = inject(ShortcutRegistryService);
    private router = inject(Router);
    private destroyRef = inject(DestroyRef);

    /** Baseline when entering edit mode; used for dirty detection. */
    private initialCustomShortcuts: Map<string, string[]> | null = null;

    @ViewChild('contentScroll') contentScrollRef!: ElementRef<HTMLElement>;
    @ViewChildren(ShortcutInputComponent) shortcutInputs!: QueryList<ShortcutInputComponent>;

    hotkeys = signal<Hotkey[]>([]);
    searchQuery = signal<string>('');
    showCustomizeMode = signal<boolean>(false);
    selectedCategory = signal<string>('');
    /** When true, show inline confirmation to discard unsaved changes instead of closing. */
    pendingCloseConfirmation = signal<boolean>(false);

    // Track custom shortcuts (map key = first combo of hotkey, value = array of combos)
    customShortcuts = signal<Map<string, string[]>>(new Map());

    // Regular property for ngModel binding
    searchInput = '';

    groupedHotkeys = computed(() => {
        const filtered = this.filterHotkeys();
        return this.groupHotkeysByCategory(filtered);
    });

    categories = computed(() => {
        return Array.from(new Set(this.groupedHotkeys().map(g => g.category))).sort();
    });

    readonly ALL_CATEGORIES = '__ALL__';

    selectedCategoryHotkeys = computed(() => {
        const category = this.selectedCategory();
        if (!category) {
            return [];
        }
        if (category === this.ALL_CATEGORIES) {
            return this.filterHotkeys();
        }
        return this.getHotkeysForCategory(category);
    });

    filteredHotkeysCount = computed(() => {
        return this.groupedHotkeys().reduce((acc, g) => acc + g.hotkeys.length, 0);
    });

    constructor() {
        // Auto-select "All Categories" when categories become available
        effect(() => {
            const cats = this.categories();
            if (cats.length > 0 && !this.selectedCategory()) {
                this.selectedCategory.set(this.ALL_CATEGORIES);
            }
        });
    }

    public ngOnInit(): void {
        this.shortcutRegistry.shortcutsChanged$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.refreshHotkeys());

        this.hotkeysService.hotkeysChanged$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.refreshHotkeys());

        this.router.events
            .pipe(
                filter((e): e is NavigationEnd => e instanceof NavigationEnd),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(() => this.refreshHotkeys());

        this.subscription = this.hotkeysService.cheatSheetToggle.subscribe(async (isOpen) => {
            if (isOpen !== false) {
                this.refreshHotkeys();
            }

            if (isOpen === false) {
                await this.closeCheatSheet();
            } else {
                await this.toggleCheatSheet();
            }
        });
    }

    public ngOnDestroy(): void {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    }

    public async toggleCheatSheet(): Promise<void> {
        if (this.helpVisible$.value) {
            await this.closeCheatSheet();
        } else {
            this.helpVisible$.next(true);
        }
    }

    private refreshHotkeys(): void {
        // Action shortcuts (keyMap) are registered into each page's own component-scoped
        // HotkeysService instance, so this cheatsheet's instance only ever holds its own
        // open/close entries. Enumerate the action shortcuts from the global keyMap instead,
        // so the help modal lists the full set regardless of which page is mounted.
        const registered = this.hotkeysService.hotkeys.filter((hotkey) => hotkey.description);
        const actionShortcuts = this.buildActionShortcutHotkeys();
        const seen = new Set<string>();
        const base = [...registered, ...actionShortcuts].filter((h) => {
            const key = this.getPersistKey(h);
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
        const isOnDocumentEditPage = this.router.url.includes('/documents/edit');

        if (!isOnDocumentEditPage) {
            this.hotkeys.set(base);
            return;
        }

        const synthetic = this.shortcutRegistry.getDocumentReportShortcutsForDisplay();
        const seenButtonIds = new Set(
            base
                .map((h) => {
                    const combo = Array.isArray(h.combo) ? h.combo[0] : h.combo;
                    return this.shortcutRegistry.getButtonIdForCombo(combo);
                })
                .filter((id): id is string => !!id)
        );

        const toAdd = synthetic.filter((h) => {
            const combo = Array.isArray(h.combo) ? h.combo[0] : h.combo;
            const buttonId = this.shortcutRegistry.getButtonIdForCombo(combo);
            if (!buttonId || seenButtonIds.has(buttonId)) return false;
            seenButtonIds.add(buttonId);
            return true;
        });

        this.hotkeys.set([...base, ...toAdd]);
    }

    /**
     * Builds display-only entries for every globally-defined action shortcut (keyMap), using the
     * current effective combos (keyMap defaults merged with the user's saved overrides). These are
     * never bound to Mousetrap — the real bindings live in each page's own HotkeysService instance;
     * this exists purely so the help modal can list the full set of shortcuts.
     */
    private buildActionShortcutHotkeys(): Hotkey[] {
        const noop = (): boolean => false;
        return (Object.keys(keyMap) as (keyof KeyActionMap)[]).map((action) => {
            const combos = this.hotkeysService.getCombos(action);
            const comboArr = Array.isArray(combos) ? combos : [combos];
            const uniqueCombos = Array.from(new Set(comboArr.filter(Boolean)));
            const description = this.translateService.instant('hotkeys.hotkey-description.' + String(action));
            const label = this.translateService.instant('hotkeys.hotkey-action-label.' + String(action));
            return new Hotkey(uniqueCombos, noop, [], description, label, false);
        });
    }

    onSearchChange(value: string): void {
        this.searchInput = value;
        this.searchQuery.set(value);
    }

    private filterHotkeys(): Hotkey[] {
        const query = this.searchQuery().toLowerCase().trim();
        if (!query) {
            return this.hotkeys();
        }

        return this.hotkeys().filter(hotkey => {
            const description = typeof hotkey.description === 'string'
                ? hotkey.description.toLowerCase()
                : '';
            const keys = hotkey.formatted.join(' ').toLowerCase();
            return description.includes(query) || keys.includes(query);
        });
    }

    /**
     * Category per action shortcut, keyed by keyMap action (locale-independent).
     * The previous approach matched English keywords ("edit", "save", …) against the
     * TRANSLATED description, so non-English locales (e.g. German: "Speichern", "Löschen")
     * collapsed almost every shortcut into "General". Keying off the action fixes it everywhere.
     */
    private static readonly ACTION_CATEGORY: Record<string, string> = {
        enterEditInline: 'hotkeys.category.editing',
        save: 'hotkeys.category.editing',
        edit: 'hotkeys.category.editing',
        enableEditInLine: 'hotkeys.category.editing',
        editInLine: 'hotkeys.category.editing',
        cancelEditInLine: 'hotkeys.category.editing',
        search: 'hotkeys.category.navigation',
        tabFromSearch: 'hotkeys.category.navigation',
        find: 'hotkeys.category.navigation',
        openTaskPanel: 'hotkeys.category.navigation',
        cancel: 'hotkeys.category.actions',
        delete: 'hotkeys.category.actions',
        deleteRow: 'hotkeys.category.actions',
        clipboardCopy: 'hotkeys.category.actions',
    };

    /** Resolves a hotkey's category translation key without depending on the display language. */
    private categoryKeyForHotkey(hotkey: Hotkey): string {
        // Action shortcuts: categorize by their action key.
        const actionCategory = HotkeysCheatsheetComponent.ACTION_CATEGORY[this.getPersistKey(hotkey)];
        if (actionCategory) return actionCategory;

        // Dynamic report / shipping-control shortcuts carry a translated prefix in their
        // description; comparing against the same translated prefix stays locale-consistent.
        const description = (typeof hotkey.description === 'string' ? hotkey.description : '').toLowerCase();
        const warehouseDispositionPrefix = (this.translateService.instant('hotkeys.category.warehouseDisposition') + ': ').toLowerCase();
        const shippingControlPrefix = (this.translateService.instant('hotkeys.category.shippingControl') + ': ').toLowerCase();
        const reportsPrefix = (this.translateService.instant('hotkeys.category.reports') + ': ').toLowerCase();
        if (description.startsWith(warehouseDispositionPrefix)) return 'hotkeys.category.warehouseDisposition';
        if (description.startsWith(shippingControlPrefix)) return 'hotkeys.category.shippingControl';
        if (description.startsWith(reportsPrefix)) return 'hotkeys.category.reports';

        return 'hotkeys.category.general';
    }

    private groupHotkeysByCategory(hotkeys: Hotkey[]): GroupedHotkey[] {
        const grouped = new Map<string, Hotkey[]>();

        hotkeys.forEach(hotkey => {
            const category = this.translateService.instant(this.categoryKeyForHotkey(hotkey));
            if (!grouped.has(category)) {
                grouped.set(category, []);
            }
            grouped.get(category)!.push(hotkey);
        });

        return Array.from(grouped.entries()).map(([category, hotkeys]) => ({
            category,
            hotkeys
        }));
    }

    getHotkeysForCategory(category: string): Hotkey[] {
        const group = this.groupedHotkeys().find(g => g.category === category);
        return group ? group.hotkeys : [];
    }

    /** Returns hotkey description as string for translate pipe (description can be string | Function in model). */
    getHotkeyDescription(hotkey: Hotkey): string {
        return typeof hotkey.description === 'string' ? hotkey.description : '';
    }

    private static readonly CHEATSHEET_DESC_OPEN = 'hotkeys.show-hotkeys';
    private static readonly CHEATSHEET_DESC_CLOSE = 'hotkeys.hide-hotkeys';

    /** Unique persist key per hotkey (description-based for cheat sheet, else buttonId/actionKey/combo). */
    getPersistKey(hotkey: Hotkey): string {
        const desc = this.getHotkeyDescription(hotkey);
        if (desc === HotkeysCheatsheetComponent.CHEATSHEET_DESC_OPEN) return 'cheatSheetOpen';
        if (desc === HotkeysCheatsheetComponent.CHEATSHEET_DESC_CLOSE) return 'cheatSheetClose';
        const combo = Array.isArray(hotkey.combo) ? hotkey.combo[0] : hotkey.combo;
        const fromCombo = this.shortcutRegistry.getButtonIdForCombo(combo)
            ?? this.hotkeysService.getActionTypeForCombo(combo)
            ?? combo;
        if (fromCombo) return fromCombo;
        // Fallback when combo is empty: match description to action key (for action shortcuts)
        for (const actionKey of Object.keys(keyMap) as (keyof KeyActionMap)[]) {
            const translated = this.translateService.instant('hotkeys.hotkey-description.' + String(actionKey));
            if (translated && desc === translated) return actionKey as string;
        }
        return desc ?? '';
    }

    private static mapsEqual(a: Map<string, string[]>, b: Map<string, string[]>): boolean {
        if (a.size !== b.size) return false;
        for (const [key, arrA] of a) {
            const arrB = b.get(key);
            if (!arrB || arrA.length !== arrB.length) return false;
            const sortedA = [...arrA].sort();
            const sortedB = [...arrB].sort();
            if (sortedA.some((v, i) => v !== sortedB[i])) return false;
        }
        return true;
    }

    private deepCopyShortcutsMap(map: Map<string, string[]>): Map<string, string[]> {
        const copy = new Map<string, string[]>();
        for (const [k, v] of map) {
            copy.set(k, [...v]);
        }
        return copy;
    }

    private hasUnsavedChanges(): boolean {
        if (!this.initialCustomShortcuts) return false;
        this.shortcutInputs?.forEach((input) => input.addCurrentCombo());
        const inputs = this.shortcutInputs?.toArray() ?? [];
        const hotkeyList = this.selectedCategory() === this.ALL_CATEGORIES
            ? this.groupedHotkeys().flatMap((g) => g.hotkeys)
            : this.selectedCategoryHotkeys();
        for (let i = 0; i < hotkeyList.length && i < inputs.length; i++) {
            const platformCombos = inputs[i].getCombosForSave();
            const currentFull = this.customShortcuts().get(this.getPersistKey(hotkeyList[i])) ?? [];
            const hidden = currentFull.filter(c => !Hotkey.filterCombosForDisplay(currentFull).includes(c));
            this.setCustomShortcut(hotkeyList[i], [...hidden, ...platformCombos]);
        }
        return !HotkeysCheatsheetComponent.mapsEqual(this.customShortcuts(), this.initialCustomShortcuts);
    }

    private resetCheatSheetState(): void {
        this.showCustomizeMode.set(false);
        this.searchQuery.set('');
        this.searchInput = '';
        this.selectedCategory.set('');
        this.initialCustomShortcuts = null;
    }

    private async closeCheatSheet(): Promise<void> {
        if (!this.helpVisible$.value) return;
        if (this.pendingCloseConfirmation()) {
            this.cancelClose();
            return;
        }
        if (this.showCustomizeMode() && this.hasUnsavedChanges()) {
            this.pendingCloseConfirmation.set(true);
            setTimeout(() => this.contentScrollRef?.nativeElement?.scrollTo({ top: 0 }), 0);
            return;
        }
        this.doCloseCheatSheet();
    }

    /** Performs the actual close and state reset. */
    private doCloseCheatSheet(): void {
        this.pendingCloseConfirmation.set(false);
        this.resetCheatSheetState();
        this.helpVisible$.next(false);
    }

    /** User confirmed discard from inline confirmation. */
    confirmDiscardAndClose(): void {
        this.doCloseCheatSheet();
    }

    /** User cancelled from inline confirmation. */
    cancelClose(): void {
        this.pendingCloseConfirmation.set(false);
    }

    toggleCustomizeMode(): void {
        const next = !this.showCustomizeMode();
        if (next) {
            const map = new Map<string, string[]>();
            for (const h of this.hotkeys()) {
                const key = this.getPersistKey(h);
                const combos = Array.isArray(h.combo) ? [...h.combo] : [h.combo];
                map.set(key, combos);
            }
            this.customShortcuts.set(map);
            this.initialCustomShortcuts = this.deepCopyShortcutsMap(map);
        } else {
            this.initialCustomShortcuts = null;
        }
        this.showCustomizeMode.set(next);
    }

    /** Whether this hotkey's shortcut is persisted (from shortcut-record-dialog); only these are saved. */
    isCustomShortcut(hotkey: Hotkey): boolean {
        const combo = Array.isArray(hotkey.combo) ? hotkey.combo[0] : hotkey.combo;
        return this.shortcutRegistry.getButtonIdForCombo(combo) !== undefined;
    }

    getCustomShortcut(hotkey: Hotkey): string[] {
        const key = this.getPersistKey(hotkey);
        return this.customShortcuts().get(key) ?? [];
    }

    getFilteredCustomShortcut(hotkey: Hotkey): string[] {
        const full = this.getCustomShortcut(hotkey);
        return Hotkey.filterCombosForDisplay(full);
    }

    onFilteredCombosChange(hotkey: Hotkey, newPlatformCombos: string[]): void {
        const full = this.getCustomShortcut(hotkey);
        const hidden = full.filter(c => !Hotkey.filterCombosForDisplay(full).includes(c));
        this.setCustomShortcut(hotkey, [...hidden, ...newPlatformCombos]);
    }

    /** Used by ShortcutInput to reject non-platform combos on Windows (allows all on Mac). */
    platformComboFilter = (combo: string): boolean =>
        Hotkey.filterCombosForDisplay([combo]).length > 0;

    setCustomShortcut(hotkey: Hotkey, combos: string[]): void {
        const key = this.getPersistKey(hotkey);
        const newMap = new Map(this.customShortcuts());
        newMap.set(key, combos);
        this.customShortcuts.set(newMap);
    }

    getExcludeButtonId(hotkey: Hotkey): string | undefined {
        const combo = Array.isArray(hotkey.combo) ? hotkey.combo[0] : hotkey.combo;
        return this.shortcutRegistry.getButtonIdForCombo(combo);
    }

    getExcludeActionKey(hotkey: Hotkey): string | undefined {
        const desc = this.getHotkeyDescription(hotkey);
        if (desc === HotkeysCheatsheetComponent.CHEATSHEET_DESC_OPEN) return 'cheatSheetOpen';
        if (desc === HotkeysCheatsheetComponent.CHEATSHEET_DESC_CLOSE) return 'cheatSheetClose';
        const combo = Array.isArray(hotkey.combo) ? hotkey.combo[0] : hotkey.combo;
        return this.hotkeysService.getActionTypeForCombo(combo);
    }

    saveCustomShortcuts(): void {
        // Auto-add any preview combos (user typed but didn't press Enter)
        this.shortcutInputs?.forEach((input) => input.addCurrentCombo());

        // Flush each input's state into customShortcuts (match dialog's getCombosForSave approach)
        const inputs = this.shortcutInputs?.toArray() ?? [];
        const hotkeyList = this.selectedCategory() === this.ALL_CATEGORIES
            ? this.groupedHotkeys().flatMap((g) => g.hotkeys)
            : this.selectedCategoryHotkeys();
        for (let i = 0; i < hotkeyList.length && i < inputs.length; i++) {
            const platformCombos = inputs[i].getCombosForSave();
            const currentFull = this.customShortcuts().get(this.getPersistKey(hotkeyList[i])) ?? [];
            const hidden = currentFull.filter(c => !Hotkey.filterCombosForDisplay(currentFull).includes(c));
            this.setCustomShortcut(hotkeyList[i], [...hidden, ...platformCombos]);
        }

        const map = this.customShortcuts();
        const skipped: string[] = [];
        const initial = this.initialCustomShortcuts ?? new Map<string, string[]>();

        /** Diff-based winner: for each combo, the entry that gained it (didn't have it in initial) wins. Losers get it removed. */
        const comboWinner = this.buildComboWinners(map, initial);

        for (const [persistKey, newCombos] of map.entries()) {
            const rawCombos = Array.isArray(newCombos)
                ? newCombos.map((c) => c?.trim().toLowerCase()).filter(Boolean)
                : [];

            if (persistKey === 'cheatSheetOpen' || persistKey === 'cheatSheetClose') {
                const combos = rawCombos.filter((c) => {
                    const norm = Hotkey.compactForStorage(c);
                    return norm && comboWinner.get(norm) === persistKey;
                });
                const allowedForCheatSheet = persistKey === 'cheatSheetOpen' ? ['f1', '?'] : ['esc'];
                const invalid = this.validateCombos(combos, undefined, persistKey, allowedForCheatSheet);
                if (invalid) {
                    skipped.push(this.getHotkeyLabelForPersistKey(persistKey) + ': ' + invalid);
                    continue;
                }
                this.hotkeysService.setCheatSheetOverride(persistKey as 'cheatSheetOpen' | 'cheatSheetClose', combos.length > 0 ? combos : null);
                continue;
            }

            /** Filter to combos this persistKey wins; losers get them excluded (removal). */
            const combos = rawCombos.filter((c) => {
                const norm = Hotkey.compactForStorage(c);
                return norm && comboWinner.get(norm) === persistKey;
            });

            if (persistKey in keyMap) {
                const invalid = this.validateCombos(combos, undefined, persistKey);
                if (invalid) {
                    skipped.push(this.getHotkeyLabelForPersistKey(persistKey) + ': ' + invalid);
                    continue;
                }
                if (combos.length === 0) {
                    this.hotkeysService.setActionShortcut(persistKey, null);
                } else {
                    for (const c of combos) {
                        this.shortcutRegistry.clearComboFromOtherButtons(c);
                    }
                    this.hotkeysService.setActionShortcut(persistKey, combos);
                }
                continue;
            }

            // Button shortcut (buttonId)
            const invalid = this.validateCombos(combos, persistKey, undefined);
            if (invalid) {
                skipped.push(persistKey + ': ' + invalid);
                continue;
            }
            if (combos.length === 0) {
                this.shortcutRegistry.setShortcut(persistKey, null);
            } else {
                this.shortcutRegistry.setShortcut(persistKey, combos);
            }
        }

        if (skipped.length > 0) {
            alert(
                this.translateService.instant(_('hotkeys.shortcuts-skipped')) + '\n' +
                skipped.join('\n')
            );
        }
        this.shortcutRegistry.shortcutsChanged$.next();
        this.showCustomizeMode.set(false);
        this.initialCustomShortcuts = null;
    }

    /**
     * For each combo in the final state, determines the winner: the entry that gained it (didn't have it in initial).
     * If no gainer but exactly one claimant, that claimant keeps it. One combo → one owner; losers get it removed.
     */
    private buildComboWinners(
        map: Map<string, string[]>,
        initial: Map<string, string[]>
    ): Map<string, string> {
        const allCombos = new Set<string>();
        for (const [pk, combos] of map.entries()) {
            for (const c of combos ?? []) {
                const norm = Hotkey.compactForStorage(c?.trim().toLowerCase());
                if (norm) allCombos.add(norm);
            }
        }

        const comboWinner = new Map<string, string>();
        for (const combo of allCombos) {
            const claimants: string[] = [];
            for (const [pk, combos] of map.entries()) {
                const normalized = (combos ?? [])
                    .map((c) => Hotkey.compactForStorage(c?.trim().toLowerCase()))
                    .filter(Boolean);
                if (normalized.includes(combo)) claimants.push(pk);
            }
            const gainers = claimants.filter((pk) => {
                const initCombos = (initial.get(pk) ?? [])
                    .map((c) => Hotkey.compactForStorage(c?.trim().toLowerCase()))
                    .filter(Boolean);
                return !initCombos.includes(combo);
            });
            if (gainers.length === 1) {
                comboWinner.set(combo, gainers[0]);
            } else if (gainers.length === 0 && claimants.length === 1) {
                comboWinner.set(combo, claimants[0]);
            } else if (claimants.length > 0) {
                comboWinner.set(combo, claimants[0]);
            }
        }
        return comboWinner;
    }

    /** Returns validation error message or null if valid. Conflicts are allowed (user can overwrite). */
    private validateCombos(
        combos: string[],
        excludeButtonId?: string,
        excludeActionKey?: string,
        excludeCombos?: string[]
    ): string | null {
        for (const normalized of combos) {
            const isSequence = !normalized.includes('+');
            if (!isSequence) {
                const parts = normalized.split('+');
                if (parts.length === 1 && parts[0].length === 1) {
                    return this.translateService.instant(_('hotkeys.shortcut-single-key'));
                }
            }
        }
        return null;
    }

    private getHotkeyLabelForPersistKey(persistKey: string): string {
        if (persistKey === 'cheatSheetOpen') return this.translateService.instant('hotkeys.show-hotkeys');
        if (persistKey === 'cheatSheetClose') return this.translateService.instant('hotkeys.hide-hotkeys');
        return persistKey;
    }

    resetCustomShortcuts(): void {
        this.hotkeysService.clearAllActionShortcuts();
        this.hotkeysService.setCheatSheetOverride('cheatSheetOpen', null);
        this.hotkeysService.setCheatSheetOverride('cheatSheetClose', null);
        this.shortcutRegistry.clearAllButtonShortcuts();
        this.shortcutRegistry.shortcutsChanged$.next();
        const map = new Map<string, string[]>();
        for (const h of this.hotkeys()) {
            const key = this.getPersistKey(h);
            const combos = Array.isArray(h.combo) ? [...h.combo] : [h.combo];
            map.set(key, combos);
        }
        this.customShortcuts.set(map);
    }

    hasCustomShortcuts(): boolean {
        return this.customShortcuts().size > 0;
    }

    selectCategory(category: string): void {
        this.selectedCategory.set(category);
    }
}
